import { ErrorMessage, Field, Form, Formik } from "formik";
import moment from "moment";
import "moment-timezone";
import React from "react";
import { Button, ControlLabel } from "react-bootstrap";
import shortid from "shortid";
import * as Yup from "yup";
import FormikDatetime from "../../../Shared/FormikDateTime";
import FormikInputControl from "../../../Shared/Forms/FormikInputControl";
import { formats } from "../../../variables/variables";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const MAX_WEEKS = 2;
const SLOTS_PER_DAY = 4; // matches the 4 blocks per day Regular Hours allows

// Patterns are anchored to the start of a week so a cycle week always lines up with a calendar
// week - a mid-week start would split one calendar week across two different sets of hours.
const nextMonday = () => {
  const monday = moment().startOf("isoWeek").add(1, "week");
  return monday;
};

const blankWeek = () => {
  const week = {};
  DAYS.forEach((day) => {
    week[day.toLowerCase()] = Array.from({ length: SLOTS_PER_DAY }, () => ({ from: "", to: "" }));
  });
  return week;
};

const timeRegex = /^$|^([0-9]|0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/;

const toMinutes = (hhmm) => {
  const parts = hhmm.split(":");
  return parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
};

// Exported for testing. Returns an error message, or null when the hours are usable.
export const validatePatternHours = (weeksUsed) => {
  let pairsValid = true;
  let badTime = false;
  let reversed = null;
  let overlap = null;
  let anyHours = false;

  weeksUsed.forEach((week, weekIndex) => {
    DAYS.forEach((day) => {
      const blocks = [];
      (week[day.toLowerCase()] || []).forEach((slot) => {
        const from = slot.from || "";
        const to = slot.to || "";
        // Same rule as Regular Hours: both ends of a block, or neither, and never equal.
        if (from.length > 0 && to.length === 0) pairsValid = false;
        if (to.length > 0 && from.length === 0) pairsValid = false;
        if (from.length > 0 && from === to) pairsValid = false;
        if (!timeRegex.test(from) || !timeRegex.test(to)) badTime = true;

        if (from.length > 0 && to.length > 0 && timeRegex.test(from) && timeRegex.test(to)) {
          anyHours = true;
          const startMin = toMinutes(from);
          const endMin = toMinutes(to);
          // The generator sorts the pair, so 22:00-02:00 would silently become 02:00-22:00.
          if (endMin < startMin) reversed = reversed || `Week ${weekIndex + 1} ${day}`;
          else blocks.push([startMin, endMin]);
        }
      });

      // Overlapping blocks generate overlapping availability periods, and the booking app
      // then counts one consultant twice in the hours they share.
      blocks.sort((a, b) => a[0] - b[0]);
      for (let i = 1; i < blocks.length; i += 1) {
        if (blocks[i][0] < blocks[i - 1][1]) overlap = overlap || `Week ${weekIndex + 1} ${day}`;
      }
    });
  });

  if (badTime) return "Hours must be in 24 hour HH:MM format, for example 13:00";
  if (!pairsValid) {
    return "Start and End hours must both be provided (or neither), and not be the same";
  }
  if (reversed) return `${reversed}: the End time must be later than the Start time`;
  if (overlap) {
    return `${overlap}: time blocks overlap each other - split them into one block per period of work`;
  }
  if (!anyHours) return "Enter hours for at least one day, otherwise use an Override";
  return null;
};

// Exported for testing. Drops blank blocks and shapes the weeks for the API.
export const buildWeeksAvailability = (weeksUsed) =>
  weeksUsed.map((week) => {
    const built = {};
    DAYS.forEach((day) => {
      built[day.toLowerCase()] = (week[day.toLowerCase()] || [])
        .map((slot) =>
          slot.from && slot.to && slot.from.length > 0 && slot.to.length > 0
            ? { from: slot.from, to: slot.to }
            : null
        )
        .filter((slot) => slot !== null);
    });
    return built;
  });

const AddMultiWeekPattern = ({ add, cancel }) => {
  const StartEnd = ({ weekIndex, day, slot, errors, touched }) => (
    <div style={{ display: "flex", marginRight: 20 }}>
      <FormikInputControl
        fieldLabel=""
        fieldName={`weeks.${weekIndex}.${day.toLowerCase()}.${slot}.from`}
        errors={errors}
        touched={touched}
        style={{ width: 60 }}
        placeholder="00:00"
        className="form-group"
      />
      <span style={{ margin: "8px 5px 0 5px" }}>to</span>
      <FormikInputControl
        fieldLabel=""
        fieldName={`weeks.${weekIndex}.${day.toLowerCase()}.${slot}.to`}
        errors={errors}
        touched={touched}
        style={{ width: 60 }}
        placeholder="00:00"
        className="form-group"
      />
    </div>
  );

  const validationSchema = Yup.object().shape({
    description: Yup.string().max(50, "max 50 chars"),
    starts_on: Yup.date().required("is required"),
    repeat_for_weeks: Yup.number()
      .transform((value, original) => (original === "" ? undefined : value))
      .typeError("must be a number")
      .integer("must be a whole number of weeks")
      .min(1, "must be at least 1 week")
      .max(104, "must be 104 weeks or less"),
  });

  const submitForm = (values, { setSubmitting, setFieldError, validateForm }) => {
    // The picker only offers Mondays, but the field is typeable so the date can still be junk.
    const startsOn = moment(values.starts_on, ["DD MMM YYYY", moment.ISO_8601], true).isValid()
      ? moment(values.starts_on, ["DD MMM YYYY", moment.ISO_8601], true)
      : moment(values.starts_on);
    if (!startsOn.isValid()) {
      setFieldError("starts_on", "is not a valid date");
      setSubmitting(false);
      return;
    }

    // A 2 week cycle told to run for 1 week would never reach Week 2.
    const runsFor = values.repeat_for_weeks === "" ? null : parseInt(values.repeat_for_weeks, 10);
    if (runsFor !== null && !isNaN(runsFor) && runsFor < values.weeks_count) {
      setFieldError(
        "form",
        `Runs For must be at least ${values.weeks_count} weeks for a ${values.weeks_count} week cycle, or blank to repeat`
      );
      setSubmitting(false);
      return;
    }

    const weeksUsed = values.weeks.slice(0, values.weeks_count);
    const hoursError = validatePatternHours(weeksUsed);
    if (hoursError) {
      setFieldError("form", hoursError);
      setSubmitting(false);
      return;
    }

    validateForm().then(() => {
      add({
        id: shortid.generate(),
        starts_on: startsOn.clone().startOf("isoWeek").format(formats.consistentDateFormat),
        repeat_for_weeks: runsFor !== null && !isNaN(runsFor) ? runsFor : null,
        description: values.description,
        weeks_availability: buildWeeksAvailability(weeksUsed),
      });
    });

    setSubmitting(false);
  };

  return (
    <div style={{ minWidth: 700 }}>
      <h4>Add Multi-Week Pattern</h4>
      <p style={{ color: "#666", maxWidth: 700 }}>
        Rotating hours that override Regular Hours from the start date. A 2 week cycle alternates
        Week 1 and Week 2. Overrides and public holidays still win over a pattern.
      </p>

      <Formik
        initialValues={{
          starts_on: nextMonday(),
          weeks_count: 2,
          repeat_for_weeks: "",
          description: "",
          weeks: Array.from({ length: MAX_WEEKS }, () => blankWeek()),
        }}
        validationSchema={validationSchema}
        onSubmit={submitForm}
        render={({ errors, touched, isSubmitting, values, setFieldValue, setFieldTouched }) => {
          const hasError = (key) => errors[key] && touched[key] && "has-error";

          return (
            <Form>
              <div style={{ display: "flex" }}>
                <div className={`form-group ${hasError("starts_on")}`} style={{ marginRight: 20 }}>
                  <ControlLabel>
                    Starting <small>(Mondays only)</small>
                  </ControlLabel>
                  <FormikDatetime
                    className="datetime-input-small"
                    field="starts_on"
                    input={{
                      value: moment(values.starts_on).format("DD MMM YYYY"),
                      onChange: setFieldValue,
                      onBlur: setFieldTouched,
                    }}
                    closeOnSelect={true}
                    dateFormat="DD MMM YYYY"
                    timeFormat={false}
                    isValidDate={(current) => current.isoWeekday() === 1}
                  />
                  <ErrorMessage name="starts_on" component="span" className="help-block" />
                </div>

                <div className="form-group" style={{ marginRight: 20 }}>
                  <ControlLabel>Cycle Length</ControlLabel>
                  <select
                    value={values.weeks_count}
                    name="weeks_count"
                    className="form-control"
                    style={{ width: 160 }}
                    onChange={(e) => setFieldValue("weeks_count", parseInt(e.target.value, 10))}
                  >
                    <option value={1}>1 week</option>
                    <option value={2}>2 weeks (alternating)</option>
                  </select>
                </div>

                <FormikInputControl
                  fieldLabel="Runs For"
                  fieldName="repeat_for_weeks"
                  errors={errors}
                  touched={touched}
                  style={{ width: 140, marginRight: 20 }}
                  placeholder="ongoing"
                  className={`form-group ${hasError("repeat_for_weeks")}`}
                />

                <div className={`form-group ${hasError("description")}`}>
                  <ControlLabel>Description</ControlLabel>
                  <Field
                    type="text"
                    name="description"
                    className="form-control input-sm"
                    style={{ width: 200 }}
                  />
                  <ErrorMessage name="description" component="span" className="help-block" />
                </div>
              </div>
              <p style={{ color: "#666", marginTop: -10 }}>
                <small>
                  Runs For is a number of weeks - leave blank to repeat until you remove it. Hours
                  revert to Regular Hours once a pattern finishes.
                </small>
              </p>

              {Array.from({ length: values.weeks_count }, (unused, weekIndex) => (
                <div key={weekIndex} style={{ marginBottom: 10 }}>
                  <h5>
                    <strong>Week {weekIndex + 1}</strong>
                  </h5>
                  <table className="table table-condensed table-zebra">
                    <tbody>
                      {DAYS.map((day) => (
                        <tr key={day}>
                          <td style={{ width: 110 }}>
                            <strong>{day}</strong>
                          </td>
                          <td>
                            <div
                              style={{
                                display: "flex",
                                flexWrap: "wrap",
                                maxWidth: 380,
                              }}
                            >
                              {Array.from({ length: SLOTS_PER_DAY }, (unusedSlot, slot) => (
                                <StartEnd
                                  key={slot}
                                  weekIndex={weekIndex}
                                  day={day}
                                  slot={slot}
                                  errors={errors}
                                  touched={touched}
                                />
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}

              <div className={`form-group ${errors["form"] && "has-error"}`}>
                <span className="help-block">{errors && errors["form"]}</span>
              </div>

              <Button onClick={cancel} bsSize="small" className="mr-2">
                Cancel
              </Button>
              <Button disabled={isSubmitting} type="submit" bsStyle="info" bsSize="small">
                Add Pattern
              </Button>
            </Form>
          );
        }}
      />
    </div>
  );
};

export default AddMultiWeekPattern;
